import {
  User,
  Sun,
  Moon,
  LogOut,
  Bot,
  Users,
  UserCog,
  Sparkles,
  Lock,
  Bell,
  HelpCircle,
  MailQuestion,
  Info,
  Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTheme } from "@/components/theme-provider";
import { useAuthStore } from "@/stores/authStore";
import { isPowerUser } from "@/types/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { consultationModeApi } from "@/api/consultationMode";
import {
  CONSULTATION_MODE_LABELS,
  CONSULTATION_MODE_DESCRIPTIONS,
  type ConsultationModeType,
} from "@/types/consultationMode";
import DidNumberList from "@/components/common/DidNumberList";

/**
 * 상담 모드별 헤더 배지 색상 — Record로 4 모드 모두 명시(컴파일 시 누락 방지).
 * 다음 모드 추가 시 TypeScript가 누락 강제.
 */
const MODE_BADGE_COLORS: Record<ConsultationModeType, string> = {
  DESK: "bg-info/15 text-info",
  AI: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  AI_ASSIST:
    "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  AI_PIPELINE:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
};

/** 상담 모드별 Popover 아이콘 — 4 모드 모두 명시 */
const MODE_ICONS: Record<ConsultationModeType, React.ElementType> = {
  DESK: Users,
  AI: Bot,
  AI_ASSIST: UserCog,
  AI_PIPELINE: Sparkles,
};

/** Popover에 렌더할 모드 순서 */
const MODE_ORDER: ConsultationModeType[] = [
  "DESK",
  "AI",
  "AI_ASSIST",
  "AI_PIPELINE",
];

export function Header() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // V1153: 현재 상담 모드 조회 — backend `/api/consultation-mode`는 ORG_USER+ 허용,
  // 모든 role이 자기 조직 모드를 조회할 수 있도록 게이트 해제.
  const { data: modeConfig } = useQuery({
    queryKey: ["consultation-mode"],
    queryFn: consultationModeApi.getConfig,
    enabled: !!user,
    staleTime: 60000,
    refetchInterval: 60000,
  });

  // "상담 모드 설정으로 이동" 버튼은 편집 권한자(POWER+)에게만
  const canEditMode = isPowerUser(user);

  const handleLogout = async () => {
    await logout();
    // Full page reload to ensure all state is reset (cache, memory, etc.)
    window.location.replace("/login");
  };

  return (
    <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-50 transition-all">
      <div className="flex items-center justify-between gap-4 px-6 py-3">
        {/* Left: Logo & Operation Mode & Theme Toggle */}
        <div className="flex items-center gap-8">
          {/* Logo removed as per user request */}

          {/* V1153: 상담 모드 + DID 표시 (모든 role 조회 가능) */}
          {user && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2 h-9">
                  <Bot className="h-4 w-4" />
                  {modeConfig?.enabled ? (
                    <Badge
                      variant="secondary"
                      className={`text-xs ${MODE_BADGE_COLORS[modeConfig.currentMode] ?? ""}`}
                    >
                      {CONSULTATION_MODE_LABELS[modeConfig.currentMode]}
                    </Badge>
                  ) : (
                    <span className="hidden sm:inline text-sm">상담 모드</span>
                  )}
                  {/* 헤더 본체: DID 1개 + overflow (큰 화면만) */}
                  <span className="hidden lg:inline-flex">
                    <DidNumberList compact compactMaxVisible={1} />
                  </span>
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0" sideOffset={8}>
                <div className="p-4 space-y-3">
                  <h4 className="font-semibold text-sm">상담 모드 종류</h4>
                  <div className="space-y-2.5">
                    {MODE_ORDER.map((mode) => {
                      const Icon = MODE_ICONS[mode];
                      return (
                        <div key={mode} className="flex items-start gap-3">
                          <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <div className="text-sm font-medium">
                              {CONSULTATION_MODE_LABELS[mode]}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {CONSULTATION_MODE_DESCRIPTIONS[mode]}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 할당된 전화번호 (DID) — 모든 role 노출 */}
                <div className="border-t p-4 space-y-2">
                  <h4 className="font-semibold text-sm">할당된 전화번호</h4>
                  <DidNumberList compact compactMaxVisible={3} />
                </div>

                {/* 설정 버튼 — POWER+ 만 (편집 가능자) */}
                {canEditMode && (
                  <div className="border-t bg-muted/50 p-3 rounded-b-md">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => navigate("/settings/consultation-mode")}
                    >
                      <Settings2 className="h-4 w-4" />
                      상담 모드 설정으로 이동
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}

          {/* Theme Toggle Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="h-9 w-9"
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">테마 전환</span>
          </Button>
        </div>

        {/* Right: Support Icons + Organization + User Name & Dropdown */}
        <div className="flex items-center gap-4">
          {/* Support Navigation Icons */}
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      location.pathname === "/announcements"
                        ? "secondary"
                        : "ghost"
                    }
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => navigate("/announcements")}
                  >
                    <Bell className="h-4 w-4" />
                    <span className="sr-only">공지사항</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>공지사항</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      location.pathname === "/faq" ? "secondary" : "ghost"
                    }
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => navigate("/faq")}
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span className="sr-only">FAQ</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>FAQ</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={
                      location.pathname.startsWith("/inquiries")
                        ? "secondary"
                        : "ghost"
                    }
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => navigate("/inquiries")}
                  >
                    <MailQuestion className="h-4 w-4" />
                    <span className="sr-only">1:1 문의</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>1:1 문의</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Divider */}
          <div className="hidden md:block h-8 w-px bg-border" />

          {/* Organization + User Name */}
          <div className="hidden md:flex flex-col items-end">
            <span className="text-sm font-medium text-muted-foreground">
              {user?.organization?.name || "조직명"}
            </span>
            <span className="text-lg font-bold">{user?.name || "사용자"}</span>
          </div>

          {/* User Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="min-w-fit"
                aria-label="사용자 메뉴"
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-card z-[100]">
              <DropdownMenuItem
                onClick={() => navigate("/change-password")}
                className="gap-2 cursor-pointer text-foreground"
              >
                <Lock className="h-4 w-4" />
                <span>비밀번호 변경</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="gap-2 cursor-pointer text-foreground"
              >
                <LogOut className="h-4 w-4" />
                <span>로그아웃</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
